<template>
  <v-app>
    <v-main class="white">
      <v-container class="main-container">
        <!-- Estado: CV no encontrado -->
        <div v-if="personaEncontrada === false" class="notfound-container">
          <div class="notfound-content">
            <p class="titulo-notfound mb-2">No hemos encontrado tu CV</p>
            <p class="texto-notfound mb-6">
              Ingresar a la Bolsa de Empleo del Gobierno de El Salvador para crearlo
            </p>
            <v-btn
              rounded
              dark
              color="#22234a"
              x-large
              class="my-text-button"
              @click="goToBolsa"
            >
              Ir a la Bolsa de Empleo
              <v-icon class="ml-2" color="white">mdi-open-in-new</v-icon>
            </v-btn>
          </div>
        </div>

        <app-loader v-if="loading"></app-loader>

        <div
          v-else-if="personaEncontrada === true"
          style="background-color: white; display: flex;"
          class="ma-0 pt-12 padding-cv"
        >
          <v-container class="pa-0 justify-center d-flex" fill-height fluid>
            <v-row id="cv">
              <!-- Columna 1 -->
              <v-col cols="12" md="4" class="d-flex justify-left justify-lg-center">
                <div class="foto-wrapper d-flex flex-column" style="width: 100%;">
                  <v-card
                    class="foto-card d-flex align-center justify-center"
                    style="background-color: #ececec"
                  >
                    <v-img
                      :src="photoSrc"
                      contain
                      class="foto-card-img"
                    />
                  </v-card>

                  <!-- Nombre solo visible en mobile/tablet -->
                  <div class="d-md-none mt-8 mb-4">
                    <p class="texto-nombre">{{ datosPersona?.nombre }}</p>
                    <v-divider class="mt-2"></v-divider>
                  </div>

                  <div class="mt-4 pe-4">
                    <div class="titulo-cv">Descripción</div>
                    <div
                      class="mt-2 texto-cv text-justify"
                      v-html="datosPersona?.descripcion || 'Sin descripción'"
                    ></div>
                  </div>

                  <div class="mt-4">
                    <div class="titulo-cv">Contacto</div>
                    <div class="texto-cv mt-2">
                      <p class="mb-0">
                        Teléfono:
                        <span class="fw-700">
                          {{ telefonoDisplay }}
                        </span>
                      </p>
                      <p class="mt-2">
                        Correo:
                        <span class="fw-700">
                          {{ datosPersona?.email }}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </v-col>

              <!-- Columna 2 -->
              <v-col cols="12" md="8" class="pl-md-5 columna-detalles">
                <!-- Nombre solo visible en desktop -->
                <div class="d-none d-md-block">
                  <p class="texto-nombre">{{ datosPersona?.nombre }}</p>
                  <v-divider></v-divider>
                </div>

                <!-- Formación y conocimientos -->
                <p class="mt-5 titulo-cv">Formación y conocimientos</p>
                <template v-if="formacionContinua.length > 0 || conocimientosValidos.length > 0">
                  <div class="texto-elementos">
                    <div
                      v-for="(formacionPersona, i) in formacionContinua"
                      :key="'formacion-' + i"
                      class="mb-2"
                    >
                      <div class="">
                        {{ formacionPersona?.FormacionEducativa?.nombre || formacionPersona?.otro_titulo }}
                      </div>
                      <div style="padding-left: 0;">
                        {{ formacionPersona?.nombre_institucion }} |
                        {{
                          formatPeriod(
                            formacionPersona.fecha_inicio,
                            formacionPersona.fecha_fin,
                            formacionPersona.estudia === true
                          )
                        }}
                      </div>
                    </div>

                    <ul class="pa-0 ma-0 mt-2 lista-sin-puntos" v-if="conocimientosValidos.length > 0">
                      <li
                        v-for="(conocimiento, i) in conocimientosValidos"
                        :key="'con-' + i"
                      >
                        {{ conocimiento?.nombre_conocimiento }}
                      </li>
                    </ul>
                  </div>
                </template>
                <p v-else class="texto-cv">Sin formación registrada</p>

                <!-- Experiencia laboral -->
                <p class="mt-5 titulo-cv">Experiencia laboral</p>
                <template v-if="datosPersona?.experiencia_laboral?.length > 0">
                  <div
                    v-for="(experiencia, i) in datosPersona?.experiencia_laboral"
                    :key="'exp-' + i"
                    class="texto-elementos mb-3"
                  >
                    <span class="fw-700" style="font-weight: 900 !important;">
                      {{ experiencia?.puesto }} — {{ experiencia?.lugar_trabajo }}
                    </span>
                    <div>
                      {{
                        formatPeriod(
                          experiencia?.fecha_inicio,
                          experiencia?.fecha_fin,
                          experiencia?.trabaja === true
                        )
                      }}
                    </div>
                  </div>
                </template>
                <p v-else class="texto-cv">Sin experiencia laboral registrada</p>

                <!-- Certificados y Diplomas -->
                <p class="mt-5 titulo-cv">Certificados y Diplomas</p>
                <template v-if="certificados.length > 0">
                  <div class="texto-elementos">
                    <ul class="pa-0 ma-0">
                      <li
                        v-for="(certificado, i) in certificados"
                        :key="'cert-' + i"
                      >
                        {{ certificado?.otro_titulo || 'Certificado' }} |
                        {{ certificado?.nombre_institucion }} |
                        {{
                          formatPeriod(
                            certificado.fecha_inicio,
                            certificado.fecha_fin,
                            certificado.estudia === true
                          )
                        }}
                      </li>
                    </ul>
                  </div>
                </template>
                <p v-else class="texto-cv">Sin certificados registrados</p>

                <!-- Cualidades y Defectos -->
                <p class="mt-5 titulo-cv">Cualidades y Defectos</p>
                <template v-if="(datosPersona?.cualidades_defectos?.cualidades?.length > 0) || (datosPersona?.cualidades_defectos?.defectos?.length > 0)">
                  <div class="texto-elementos">
                    <!-- Cualidades -->
                    <template v-if="datosPersona?.cualidades_defectos?.cualidades?.length > 0">
                      <ul class="pa-0 ma-0">
                        <li
                          v-for="(cualidad, i) in datosPersona?.cualidades_defectos?.cualidades"
                          :key="'cualidad-' + i"
                        >
                          {{ cualidad?.nombre }}
                        </li>
                      </ul>
                    </template>
                    <!-- Defectos -->
                    <template v-if="datosPersona?.cualidades_defectos?.defectos?.length > 0">
                      <ul class="pa-0 ma-0" :class="{'mt-2': datosPersona?.cualidades_defectos?.cualidades?.length > 0}">
                        <li
                          v-for="(defecto, i) in datosPersona?.cualidades_defectos?.defectos"
                          :key="'defecto-' + i"
                        >
                          {{ defecto?.nombre }}
                        </li>
                      </ul>
                    </template>
                  </div>
                </template>
                <p v-else class="texto-cv">Sin cualidades o defectos registrados</p>

              </v-col>
            </v-row>
            
          </v-container>
        </div>
      </v-container>

      <!-- Footer -->
      <v-footer class="justify-center white">
        <BolsaAppFooter></BolsaAppFooter>
      </v-footer>
    </v-main>

    <!-- Error dialog -->
    <v-dialog v-model="showError" max-width="400" persistent>
      <v-card>
        <v-card-title class="text-h5 justify-center">
          <v-icon color="error" large class="mr-2">mdi-alert-circle</v-icon>
          Error
        </v-card-title>
        <v-card-text class="text-center">
          {{ errorMessage }}
        </v-card-text>
        <v-card-actions class="justify-center pb-4">
          <v-btn color="#22234a" dark @click="goToHome">
            Ir al inicio
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import AppLoader from "@/components/AppLoader.vue";
import BolsaAppFooter from "@/components/BolsaAppFooter.vue";

export default {
  name: "PublicCvView",
  components: {
    AppLoader,
    BolsaAppFooter
  },
  data: () => ({
    datosPersona: null,
    loading: false,
    personaEncontrada: null,
    bolsaClienteUrl: "",
    showError: false,
    errorMessage: "",
    defaultPhoto: require("@/assets/img/default_user.png")
  }),
  computed: {
    formacionContinua() {
      if (!this.datosPersona?.formacion) return [];
      return this.datosPersona.formacion.filter(
        (f) => f?.TipoFormacion?.nombre === "Formación continua"
      );
    },
    certificados() {
      if (!this.datosPersona?.formacion) return [];
      return this.datosPersona.formacion.filter(
        (f) => f?.TipoFormacion?.nombre === "Certificados"
      );
    },
    conocimientosValidos() {
      if (!this.datosPersona?.conocimiento) return [];
      return this.datosPersona.conocimiento.filter(
        (c) => c?.nombre_conocimiento && c.nombre_conocimiento.trim() !== ""
      );
    },
    photoSrc() {
      if (this.datosPersona?.foto) {
        return `data:image/png;base64,${this.datosPersona.foto}`;
      }
      if (this.datosPersona?.ruta_foto) {
        return this.datosPersona.ruta_foto;
      }
      return this.defaultPhoto;
    },
    telefonoDisplay() {
      if (!this.datosPersona?.telefono) return "";
      return `+503 ${this.formatPhoneNumber(this.datosPersona.telefono)}`;
    }
  },
  methods: {
    formatMonthYear(date) {
      if (!date) return "";
      const m = this.moment(date);
      if (!m || !m.isValid()) return "";
      const formatted = m.locale("es").format("MMMM YYYY");
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    },
    formatPeriod(start, end, isCurrent) {
      if (!start) return "";

      const startMoment = this.moment(start);
      const endMoment = end ? this.moment(end) : null;

      if (!startMoment.isValid()) return "";

      if (isCurrent) {
        return `${this.formatMonthYear(start)} — Actualmente`;
      }

      if (!endMoment || !endMoment.isValid()) {
        return this.formatMonthYear(start);
      }

      if (startMoment.isSame(endMoment, "month")) {
        return this.formatMonthYear(start);
      }

      return `${this.formatMonthYear(start)} — ${this.formatMonthYear(end)}`;
    },
    formatPhoneNumber(phoneNumber) {
      if (!phoneNumber) return "";
      if (phoneNumber.length === 9 && phoneNumber[4] === "-") {
        return phoneNumber;
      }
      return phoneNumber.slice(0, 4) + "-" + phoneNumber.slice(4);
    },
    async cargarDatosPublicos() {
      const dui = this.$route.params.dui;
      
      if (!dui) {
        this.showError = true;
        this.errorMessage = "No se proporcionó un DUI válido";
        return;
      }

      try {
        const response = await this.services.datosPersona.getPerfilPublico(dui);
        
        if (response.status === 200) {
          const data = response.data;

          // Validación prioritaria: persona_encontrada
          if (data && Object.prototype.hasOwnProperty.call(data, "persona_encontrada")) {
            if (data.persona_encontrada === false) {
              this.personaEncontrada = false;
              this.bolsaClienteUrl = data.bolsa_cliente || "/inicio";
              return;
            }
          }

          this.personaEncontrada = true;
          this.datosPersona = data;
        //   console.log("datos persona", this.datosPersona);
        }
      } catch (error) {
        this.showError = true;
        this.errorMessage = "No se encontró el perfil solicitado";
      }
    },
    goToBolsa() {
      const url = this.bolsaClienteUrl || "/inicio";
      window.location.assign(url);
    },
    goToHome() {
      this.$router.push("/inicio");
    }
  },
  created() {
    this.loading = true;
    setTimeout(async () => {
      await this.cargarDatosPublicos();
      this.loading = false;
    }, 1000);
  }
};
</script>

<style scoped>

.main-container {
    height: 100%;
    overflow-y: auto;
}

.notfound-container {
  min-height: calc(100vh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
}

.notfound-content {
  width: 100%;
  max-width: 720px;
  text-align: center;
}
#cv .foto-wrapper {
  width: 100%;
  max-width: 600px;
}

#cv .foto-card {
  width: 100%;
  padding: 10px;
  max-width: 356px;
  max-height: 284px;
  border-radius: 34px;
  overflow: hidden;
  background-color: #ececec;
}

#cv .foto-card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.titulo-cv {
  font-weight: 700;
  font-size: 24px;
  color: #1c1e4d;
}

.texto-cv {
  font-weight: 400;
  font-size: 16px;
  color: #616161;
}

.titulo-notfound {
  font-weight: 700;
  font-size: 30px;
  color: #1c1e4d;
}

.texto-notfound {
  font-weight: 400;
  font-size: 20px;
  color: #616161;
}

.texto-nombre {
  font-size: 40px;
  font-weight: 700;
  color: #1c1e4d;
}

.texto-elementos {
  font-size: 16px;
  color: #616161;
}

.texto-subtitulo {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-top: -4px;
}

.fw-700 {
  font-weight: 700;
}

#cv .texto-elementos ul {
  padding-left: 0;
  margin: 0;
  list-style-position: inside;
}

.lista-sin-puntos {
  list-style: none !important;
}

.columna-detalles {
  overflow-y: hidden !important;
  overflow-x: hidden !important;
  max-height: 100%;
}

@media (max-width: 960px) {
  .padding-cv {
    padding-left: 5vw !important;
    padding-right: 5vw !important;
  }

  .foto-card {
    max-width: 300px;
  }

  #cv .foto-wrapper {
    max-width: 100%;
  }

  #cv .foto-card {
    margin-left: auto;
    margin-right: auto;
  }

  .texto-nombre {
    font-size: 28px;
  }
}
</style>

